import { useMatchRoute } from '@tanstack/react-router'
import { createContext, memo, type ReactNode, useContext } from 'react'

import { ChatProvider, useDialogVisibility } from '@orus.eu/pharaoh'

import { PharaohNextScope } from '../../lib/pharaoh-next/pharaoh-next-scope.tsx'
import { HelpContactDialog } from '../../modules/support/shared/help-contact-dialog/help-contact-dialog.tsx'

const ChatWrapperProvider = createContext<boolean>(false)

export const ChatWrapper = memo<{ children: ReactNode }>(function ChatWrapper({ children }) {
  const { visible, show: openChat, hide } = useDialogVisibility('chat')
  const matchRoute = useMatchRoute()

  const isAlreadyWrapped = useContext(ChatWrapperProvider)

  // The `ChatWrapper` is already added to the root route, but if the root route itself crashes (e.g. wrong search parameters)
  // it's not wrapped. Therefore, we also wrap it in the root error boundary, so it's possible that it's wrapped twice.
  // Therefore, we add this safety layer
  if (isAlreadyWrapped) {
    return children
  }

  // The dialog is mounted above the front-office `PharaohNextScope` (it opens from back-office error
  // screens too), so it carries its own scope: without one it has neither tokens nor a portal container.
  // The surface can't come from a provider — this component sits above both shells — so it is resolved
  // against the route tree, which also answers before the matches load, i.e. when the root route crashed.
  const surface = matchRoute({ to: '/bo', fuzzy: true, includeSearch: false }) ? 'bo' : 'fo'

  return (
    <ChatWrapperProvider.Provider value={true}>
      <ChatProvider value={openChat}>{children}</ChatProvider>
      <PharaohNextScope surface={surface}>
        <HelpContactDialog open={visible} onClose={hide} />
      </PharaohNextScope>
    </ChatWrapperProvider.Provider>
  )
})
